import { ElMessageBox, ElNotification } from "element-plus";
import {
  getRequest,
  putRequest,
  postRequest,
  deleteRequest,
} from "~/apis/requests/useApiCall";
import { HelpDeskSettings } from "types/helpDeskSettings";
import {
  ProfileCustomField,
  CreateProfileCustomFieldPayload,
  UpdateProfileCustomFieldPayload,
} from "types/profileCustomFields";
import { CategoryType } from "~/types/enums/categoryType";
import {
  LeadCustomField,
  CreateLeadCustomFieldPayload,
  UpdateLeadCustomFieldPayload,
} from "~/types/leadCustomField";
import { Category } from "~/types/category";
import {
  BrandName,
  CallClosingStatus,
} from "~/projects/cc/types/settings/CallSettings";
import { OrganizationStructure } from "~/types/globalConfig";
import { CrmSettings } from "~/types/crmSettings";
import { TaskManagerSetting } from "~/projects/task-manager/types/task";

function showCustomFieldError(error: any, action: string): void {
  const errorData = error?.response?.data;

  let message = `An error occurred while ${action} the custom field.`;

  if (errorData?.errors) {
    message = Object.values(errorData.errors).flat().join("<br/>");
  } else if (errorData?.message) {
    message = errorData.message;
  }

  ElMessageBox({
    title: "Custom Field Error",
    message,
    type: "error",
    dangerouslyUseHTMLString: true,
  });
}

// Custom-field create/update/delete don't mutate store state, so they live as
// standalone helpers (keeping them as Pinia actions collapses the store's `this`
// type inference on this large store). Components call getXCustomFields() to refresh.
export async function createLeadCustomField(
  data: CreateLeadCustomFieldPayload,
): Promise<boolean> {
  const { error } = await postRequest<{ data: LeadCustomField }>(
    "custom-fields",
    { ...data, model_type: "lead" },
  );

  if (error) {
    showCustomFieldError(error, "creating");
    return false;
  }

  return true;
}

export async function updateLeadCustomField(
  id: number,
  data: UpdateLeadCustomFieldPayload,
): Promise<boolean> {
  const { error } = await putRequest<{ data: LeadCustomField }>(
    `custom-fields/${id}`,
    data,
  );

  if (error) {
    showCustomFieldError(error, "updating");
    return false;
  }

  return true;
}

export async function deleteLeadCustomField(id: number): Promise<boolean> {
  const response = await deleteRequest(`custom-fields/${id}`);

  if (response?.status !== 200 && response?.status !== 204) {
    showCustomFieldError(response, "deleting");
    return false;
  }

  return true;
}

export async function createProfileCustomField(
  data: CreateProfileCustomFieldPayload,
): Promise<boolean> {
  const { error } = await postRequest<{ data: ProfileCustomField }>(
    "custom-fields",
    { ...data, model_type: "profile" },
  );

  if (error) {
    showCustomFieldError(error, "creating");
    return false;
  }

  return true;
}

export async function updateProfileCustomField(
  id: number,
  data: UpdateProfileCustomFieldPayload,
): Promise<boolean> {
  const { error } = await putRequest<{ data: ProfileCustomField }>(
    `custom-fields/${id}`,
    data,
  );

  if (error) {
    showCustomFieldError(error, "updating");
    return false;
  }

  return true;
}

export async function deleteProfileCustomField(id: number): Promise<boolean> {
  const response = await deleteRequest(`custom-fields/${id}`);

  if (response?.status !== 200 && response?.status !== 204) {
    showCustomFieldError(response, "deleting");
    return false;
  }

  return true;
}

// Reorder persists the new sequence of saved custom fields for a model type.
// `newOrder` must list every saved field id for that model type exactly once.
export async function reorderCustomFields(
  modelType: "lead" | "profile",
  newOrder: number[],
): Promise<boolean> {
  const { error } = await postRequest<{ data: unknown }>(
    "custom-fields/reorder",
    { model_type: modelType, new_order: newOrder },
  );

  if (error) {
    showCustomFieldError(error, "reordering");
    return false;
  }

  return true;
}

export const useSettingsStore = definePiniaStore("commonSettings", {
  state: () => ({
    _profileCustomFields: [] as ProfileCustomField[],
    _leadCustomFields: [] as LeadCustomField[],
    _breakStatuses: [] as string[],
    _helpDeskSettings: {} as HelpDeskSettings,
    _callCategories: [] as Category[],
    _callClosingStatuses: [] as CallClosingStatus[],
    _organizationCategories: [] as Category[],
    _productCategories: [] as Category[],
    _leadFollowupCategories: [] as Category[],
    _autoDialCategories: [] as Category[],
    _partnerCategories: [] as Category[],
    _taskCategories: [] as Category[],
    _ticketCategories: [] as Category[],
    _galleryCategories: [] as Category[],
    _replyTemplateCategories: [] as Category[],
    _paymentItemCategories: [] as Category[],
    _paymentItemGroups: [] as Category[],
    _paymentItemTypes: [] as Category[],
    _computedCallCategories: {},
    _callBrandNames: [] as BrandName[],
    _organizationSettings: {} as OrganizationStructure,
    _crmSettings: {} as CrmSettings,
    _taskManagerSettings: {} as TaskManagerSetting,
  }),

  actions: {
    async getProfileCustomFields() {
      const { response, error } = await getRequest<ProfileCustomField[]>(
        "custom-fields?model_type=profile",
      );

      if (!error) {
        this._profileCustomFields = response.data;
      }
    },
    async getLeadCustomFields() {
      const { response, error } = await getRequest<LeadCustomField[]>(
        "custom-fields?model_type=lead",
      );

      if (!error) {
        this._leadCustomFields = response.data;
      }
    },
    async getGalleryCategories(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.gallery_item}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        this._galleryCategories = data;
      }
    },

    async getReplyTemplateCategories(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.reply_template}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        this._replyTemplateCategories = data;
      }
    },

    async getPaymentItemCategories(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.payment_item_category}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        this._paymentItemCategories = response.data;
      }
    },

    async getPaymentItemGroups(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.payment_item_group}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        this._paymentItemGroups = response.data;
      }
    },

    async getPaymentItemTypes(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.payment_item_type}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        this._paymentItemTypes = response.data;
      }
    },

    async getBreakStatuses() {
      const { response, error } = await getRequest<any>(
        "settings/break-status",
      );
      if (!error) {
        this._breakStatuses = response.data;
      }
    },

    async getCrmSettings() {
      const { response, error } = await getRequest<CrmSettings>("settings/crm");
      if (!error) {
        this._crmSettings = response.data;
      }
    },
    async updateBulkTicket(data: {
      from_date?: string;
      to_date?: string;
      status: "solved" | "closed";
      channel_id?: number;
      channel_type?: string;
      source_type?: string;
    }) {
      const { response, error } = await putRequest<{ message: string }>(
        "settings/tickets/bulk-update",
        data,
      );

      if (!error) {
        return response.data.message;
      }

      return null;
    },

    async updateCrmSettings(data: any) {
      const { response, error } = await putRequest<CrmSettings>(
        "settings/crm-settings",
        data,
      );
    },
    async getTaskManagerSettings() {
      const { response, error } = await getRequest<TaskManagerSetting>(
        "settings/task-manager",
      );
      if (!error) {
        this._taskManagerSettings = response.data;
      }
    },
    async updateTaskManagerSettings(data: any) {
      const { response, error } = await putRequest<TaskManagerSetting>(
        "settings/task-manager-settings",
        data,
      );
    },
    async getCategories(category_type: CategoryType, hierarchy = false) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${category_type}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        return data;
      }
    },
    async getCallCategories(hierarchy = false) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.call_history}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        this._callCategories = data;
      }
    },
    async addCallClosingStatus(data: Object) {
      const { response, error } = await postRequest<any>(
        "cc/call_closing_statuses",
        data,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async getCallClosingStatuses() {
      const { response, error } = await getRequest<CallClosingStatus[]>(
        "cc/call_closing_statuses",
      );
      if (!error) {
        const data = response.data;
        this._callClosingStatuses = data;
      }
    },
    async updateBreakStatuses(data: any) {
      const { response, error } = await putRequest<any>(
        "settings/break-status",
        { break_statuses: data },
      );
      if (!error) {
        this._breakStatuses = response.data;
      }
    },
    async getHelpDeskSettings() {
      const { response, error } = await getRequest<HelpDeskSettings>(
        "settings/help-desk-settings",
      );
      if (!error) {
        this._helpDeskSettings = response.data;
      }
    },
    async updateHelpDeskSettings(data: any) {
      const { error } = await putRequest<any>(
        "settings/help-desk-settings",
        data,
      );
      if (!error) {
        // The endpoint answers with a status message, not the settings. Assigning
        // response.data replaced the whole object with { message: "..." }, so the
        // next panel to read a field off the store found it undefined.
        this._helpDeskSettings = { ...this._helpDeskSettings, ...data };
      }
      // So a panel can tell a failed save from a successful one instead of
      // showing "Updated" either way. Existing callers ignore it.
      return !error;
    },

    // async getHelpDeskSettings() {
    //   const { response, error } = await getRequest<HelpDeskSettings>(
    //     "settings/help-desk-settings"
    //   );
    //   if (!error) {
    //     this._helpDeskSettings = response.data;
    //   }
    // },
    async updateCallClosingStatus(id: number, name: Object) {
      const { response, error } = await putRequest<CallClosingStatus>(
        `cc/call_closing_statuses/${id}`,
        name,
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async getCallBrandNames() {
      const { response, error } = await getRequest<BrandName[]>(
        "cc/call_brands",
      );
      if (!error) {
        const data = response.data;
        this._callBrandNames = data;
      }
    },
    async addCallBrandNames(brand: string) {
      const { response, error } = await postRequest<BrandName>(
        "cc/call_brands",
        {
          name: brand,
        },
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async updateCallBrandName(data: any) {
      const { response, error } = await putRequest<any>(
        `cc/call_brands/${data.id}`,
        {
          name: data.name,
        },
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async deleteCallBrandName(data: BrandName) {
      const { response, error } = await deleteRequest(
        `cc/call_brands/${data.id}`,
      );

      if (error) {
        throw error;
      }

      return response?.status === 204;
    },
    async getOrgCategories(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.organization}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        this._organizationCategories = data;
      }
    },

    async getProductCategories(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.product}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        this._productCategories = data;
      }
    },

    async getLeadFollowupCategories(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.lead_follow_up_category}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        this._leadFollowupCategories = data;
      }
    },

    async getAutoDialCategories(hierarchy = false) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.auto_dial_category}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        this._autoDialCategories = data;
      }
    },

    async getPartnerCategories(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.partner}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        this._partnerCategories = data;
      }
    },

    async getTaskCategories(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.task}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        this._taskCategories = data;
      }
    },
    async getTicketCategories(hierarchy = true) {
      const { response, error } = await getRequest<Category[]>(
        `categories?type=${CategoryType.ticket}&hierarchy=${hierarchy}`,
      );
      if (!error) {
        const data = response.data;
        this._ticketCategories = data;
      }
    },

    async addCategory(category_type: CategoryType, data: string) {
      const { response, error } = await postRequest<any>(
        `categories?type=${category_type}`,
        {
          name: data,
        },
      );
      if (!error) {
        return response.data;
      }
      return error.response?.data;
    },

    async addSubCategory(category_type: CategoryType, data: Object) {
      const { response, error } = await postRequest<any>(
        `categories?type=${category_type}`,
        data,
      );
      if (!error) {
        return response.data;
      }
      return error.response?.data;
    },
    async deleteCategory(categoryId: number) {
      return await deleteRequest(`categories/${categoryId}`);
    },

    async updateCategory(
      id: number,
      category_type: CategoryType,
      name: Object,
    ) {
      const { response, error } = await putRequest<any>(
        `categories/${id}?type=${category_type}`,
        name,
      );
      if (!error) {
        return response.data;
      }
      return error.response?.data;
    },

    async setComputedCallCategories() {
      await this.getCallCategories();
      await this.getCallClosingStatuses();
      this._computedCallCategories = this._callCategories
        .filter((category) => category.parent_id == null)
        .map((parent) => {
          const call_closing_status_children = this._callClosingStatuses
            .filter((statusItem) => statusItem.category_id === parent.id)
            .map((statusItem) => ({
              id: statusItem.id,
              name: statusItem.name,
            }));

          const call_categories_children = this._callCategories
            .filter((category) => category.parent_id === parent.id)
            .map((child) => {
              const gc_children = this._callCategories
                .filter((category) => category.parent_id === child.id)
                .map((grandChild) => ({
                  id: grandChild.id,
                  name: grandChild.name,
                }));

              return {
                id: child.id,
                name: child.name,
                call_categories_children: gc_children,
              };
            });

          return {
            id: parent.id,
            name: parent.name,
            call_categories_children,
            call_closing_status_children,
          };
        });
    },

    async getOrganizationSettings() {
      const { response, error } = await getRequest<OrganizationStructure>(
        "settings/organization-settings",
      );
      if (!error) {
        this._organizationSettings = response.data;
      }
    },
    async updateOrganizationSettings(data: FormData) {
      const { response, error } = await postRequest<any>(
        "settings/organization-settings",
        data,
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (error) {
        const apiError = error.response?.data;
        const errors: Record<string, string[]> = apiError?.errors || {};

        let formattedErrors = "";

        if (Object.keys(errors).length > 0) {
          formattedErrors = `<ul style="list-style-type: disc; padding-left: 20px;">`;

          Object.values(errors).forEach((messages) => {
            messages.forEach((message) => {
              formattedErrors += `<li>${message}</li>`;
            });
          });

          formattedErrors += "</ul>";
        }

        ElMessageBox({
          title: "Organization Settings Update Failed",
          message: formattedErrors || apiError?.message || error.message,
          dangerouslyUseHTMLString: true,
          type: Object.keys(errors).length > 0 ? "warning" : "error",
        });

        return false;
      }

      this._organizationSettings = response.data;
      ElNotification({
        title: "Success",
        message: "Organization settings updated.",
        type: "success",
      });
      return true;
    },
  },

  getters: {
    profileCustomFields: (state) => state._profileCustomFields,
    leadCustomFields: (state) => state._leadCustomFields,
    breakStatuses: (state) => state._breakStatuses,
    helpDeskSettings: (state) => state._helpDeskSettings,
    callCategories: (state) => state._callCategories,
    callClosingStatuses: (state) => state._callClosingStatuses,
    computedCallCategories: (state) => state._computedCallCategories,
    organizationCategories: (state) => state._organizationCategories,
    productCategories: (state) => state._productCategories,
    leadFollowupCategories: (state) => state._leadFollowupCategories,
    autoDialCategories: (state) => state._autoDialCategories,
    partnerCategories: (state) => state._partnerCategories,
    taskCategories: (state) => state._taskCategories,
    ticketCategories: (state) => state._ticketCategories,
    galleryCategories: (state) => state._galleryCategories,
    replyTemplateCategories: (state) => state._replyTemplateCategories,
    paymentItemCategories: (state) => state._paymentItemCategories,
    paymentItemGroups: (state) => state._paymentItemGroups,
    paymentItemTypes: (state) => state._paymentItemTypes,
    callBrandNames: (state) => state._callBrandNames,
    organizationSettings: (state) => state._organizationSettings,
    crmSettings: (state) => state._crmSettings,
    taskManagerSettings: (state) => state._taskManagerSettings,
  },
});
